import { ReserveHttp } from '@/services';

export function usesSequentialCancel(reserve: UserWeekReserveEntity): boolean {
  return Object.keys(reserve.idAndConsumption ?? {}).length > 0;
}

/** All distinct reserve ids for sequential (distinct delivery) cancel — parent id + map keys, each once. */
export function getDistinctDeliveryCancelIds(reserve: UserWeekReserveEntity): number[] {
  const mapIds = Object.keys(reserve.idAndConsumption ?? {})
    .map((key) => Number(key))
    .filter((id) => !Number.isNaN(id) && id > 0);

  if (reserve.id && !mapIds.includes(reserve.id)) {
    return [reserve.id, ...mapIds];
  }

  return mapIds;
}

export async function cancelDistinctDeliveryReserves(
  reserve: UserWeekReserveEntity,
  count: number,
): Promise<void> {
  if (count <= 0) return;

  const sortedIds = getDistinctDeliveryCancelIds(reserve).sort((a, b) => a - b);
  const toCancel = sortedIds.slice(-Math.min(count, sortedIds.length));

  for (const reserveId of toCancel) {
    await ReserveHttp.cancelByReserveId(reserveId);
  }
}

export async function cancelReserveCompletely(args: {
  reserve: UserWeekReserveEntity;
  meal: SelfMealEntity;
  selectedCount: number;
  apiVersion: string | null;
}): Promise<void> {
  const { reserve, selectedCount, apiVersion } = args;

  if (usesSequentialCancel(reserve)) {
    const sortedIds = getDistinctDeliveryCancelIds(reserve).sort((a, b) => a - b);
    for (const reserveId of sortedIds) {
      await ReserveHttp.cancelByReserveId(reserveId);
    }
    return;
  }

  if (apiVersion !== null && reserve.id) {
    await ReserveHttp.cancelByReserveId(reserve.id);
    return;
  }

  await ReserveHttp.cancel(reserve.programId, {
    foodTypeId: reserve.foodTypeId,
    mealTypeId: reserve.mealTypeId,
    selectedCount,
    freeFoodSelected: false,
  });
}

export async function applyReserveCountDecrease(args: {
  reserve: UserWeekReserveEntity;
  meal: SelfMealEntity;
  currentCount: number;
  targetCount: number;
  apiVersion: string | null;
  isNewReserveMode: boolean;
}): Promise<void> {
  const { reserve, meal, currentCount, targetCount, apiVersion, isNewReserveMode } = args;
  const cancelCount = currentCount - targetCount;
  if (cancelCount <= 0) return;

  if (usesSequentialCancel(reserve)) {
    await cancelDistinctDeliveryReserves(reserve, cancelCount);
    return;
  }

  await cancelReserveCompletely({ reserve, meal, selectedCount: currentCount, apiVersion });
  if (targetCount > 0) {
    await reserveAbsoluteCount({ reserve, meal, count: targetCount, isNewReserveMode });
  }
}

export async function reserveAbsoluteCount(args: {
  reserve: UserWeekReserveEntity;
  meal: SelfMealEntity;
  count: number;
  isNewReserveMode: boolean;
}): Promise<void> {
  const { reserve, count, isNewReserveMode } = args;
  if (count <= 0) return;

  if (isNewReserveMode) {
    await ReserveHttp.reserveNewAPI(reserve.programId, { count });
    return;
  }

  await ReserveHttp.reserve(reserve.programId, {
    foodTypeId: reserve.foodTypeId,
    mealTypeId: reserve.mealTypeId,
    selectedCount: count,
    freeFoodSelected: false,
  });
}

export async function reserveIncreaseCount(args: {
  reserve: UserWeekReserveEntity;
  meal: SelfMealEntity;
  currentCount: number;
  targetCount: number;
  isNewReserveMode: boolean;
}): Promise<void> {
  const { reserve, currentCount, targetCount, isNewReserveMode } = args;
  const delta = targetCount - currentCount;
  if (delta <= 0) return;

  if (isNewReserveMode) {
    await ReserveHttp.reserveNewAPI(reserve.programId, { count: delta });
    return;
  }

  await ReserveHttp.reserve(reserve.programId, {
    foodTypeId: reserve.foodTypeId,
    mealTypeId: reserve.mealTypeId,
    selectedCount: targetCount,
    freeFoodSelected: false,
  });
}
